import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';
import {
    SmallButtonWrapper,
    TimerContentContainer,
    TimerControls,
    TimerDisplay,
    TimerLabel,
    TimerSection,
    TimerTime,
    TimerTitleWrapper,
    TimerToggleIcon,
    TitleWithIcon
} from '../custom-styles/TimerSection.styles';

import COLORS from 'shared/config/colors';
import SecondaryButton from 'shared/components/inputs/button/SecondaryButton';
import TertiaryButton from 'shared/components/inputs/button/TertiaryButton';
import Text, { VARIANTS } from 'shared/components/data-display/text/Text';
import TimerIcon from '@material-ui/icons/Timer';

const TimerSectionComponent = forwardRef(function TimerSectionComponent({ onUseTimerHours }, ref) {
    // Internal state
    const [isTimerExpanded, setIsTimerExpanded] = useState(false);
    const [isTimerRunning, setIsTimerRunning] = useState(false);
    const [elapsedTime, setElapsedTime] = useState(0);
    const startTimeRef = React.useRef(null);
    const accumulatedRef = React.useRef(0);

    // Timer effect — anchored to wall clock to avoid setInterval drift
    useEffect(() => {
        let interval;
        if (isTimerRunning) {
            startTimeRef.current = Date.now();
            interval = setInterval(() => {
                const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
                setElapsedTime(accumulatedRef.current + elapsed);
            }, 500);
        }
        return () => clearInterval(interval);
    }, [isTimerRunning]);

    // Format elapsed time to HH:MM:SS
    const formatTime = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    };

    // Round to multiples of 0.25 (every 15 minutes)
    const calculateRoundedHours = () => {
        const hours = elapsedTime / 3600;
        // 0.25 hours = 15 minutes = 900 seconds
        return (Math.round(hours / 0.25) * 0.25).toFixed(2);
    };

    // Timer functions
    const startTimer = () => {
        setIsTimerRunning(true);
    };

    const pauseTimer = () => {
        accumulatedRef.current = elapsedTime;
        setIsTimerRunning(false);
    };

    const resetTimer = () => {
        accumulatedRef.current = 0;
        setIsTimerRunning(false);
        setElapsedTime(0);
    };

    // Expose reset method to parent via ref
    useImperativeHandle(ref, () => ({
        reset: resetTimer
    }));

    // Handle using rounded timer hours
    const handleUseTimerHours = () => {
        const totalHours = calculateRoundedHours();
        onUseTimerHours(totalHours);
    };

    return (
        <TimerSection>
            <TimerTitleWrapper onClick={() => setIsTimerExpanded(!isTimerExpanded)}>
                <TitleWithIcon>
                    <TimerIcon />
                    <Text variant={VARIANTS.Headline_4} color={COLORS.Black}>
                        Timer
                    </Text>
                </TitleWithIcon>
                <TimerToggleIcon expanded={isTimerExpanded ? 1 : 0} />
            </TimerTitleWrapper>
            <TimerContentContainer expanded={isTimerExpanded ? 1 : 0}>
                <TimerDisplay>
                    <TimerTime>{formatTime(elapsedTime)}</TimerTime>
                    <TimerLabel>Hours:Minutes:Seconds</TimerLabel>
                </TimerDisplay>
                <TimerControls>
                    <SmallButtonWrapper>
                        {isTimerRunning ? (
                            <SecondaryButton
                                leadingIcon="pause"
                                onClick={pauseTimer}
                                aria-label="Pause timer"
                            />
                        ) : (
                            <SecondaryButton
                                leadingIcon="play"
                                onClick={startTimer}
                                aria-label="Start timer"
                            />
                        )}
                    </SmallButtonWrapper>
                    <SmallButtonWrapper>
                        <TertiaryButton
                            leadingIcon="reset"
                            onClick={resetTimer}
                            disabled={elapsedTime === 0}
                            aria-label="Reset timer"
                        />
                    </SmallButtonWrapper>
                </TimerControls>
                <SmallButtonWrapper>
                    <SecondaryButton
                        label={`Use Timer Hours (${calculateRoundedHours()}h)`}
                        onClick={handleUseTimerHours}
                        disabled={elapsedTime === 0}
                        fullWidth={true}
                    />
                </SmallButtonWrapper>
            </TimerContentContainer>
        </TimerSection>
    );
});

TimerSectionComponent.propTypes = {
    onUseTimerHours: PropTypes.func,
};

export default TimerSectionComponent;
